import { AlertTriangle, Boxes, PackageCheck, RotateCcw, Truck, Undo2, Users } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import type {
  ComponentFlowPayload,
  InventoryRow,
  OrderType,
  OrdersByTypeRow,
  SupplyKpis,
} from "@busright/data-contract";
import { COLORS, EChart, type EChartOption, sequentialColors } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { ClickableCard } from "@/components/ClickableCard";
import { DataTable } from "@/components/DataTable";
import { Distribution, type DistributionBin } from "@/components/Distribution";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout, type InsightStat } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { MetricLabel } from "@/components/MetricTooltip";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { ScComponentPurchases } from "@/components/ScComponentPurchases";
import { ScFieldInventory } from "@/components/ScFieldInventory";
import { ScReplacementFlow } from "@/components/ScReplacementFlow";
import { useScope } from "@/context/ScopeContext";
import { useComponentFlow, useOverview, useSupply, useTelemetry } from "@/hooks/queries";
import { formatInt } from "@/lib/format";

// Supply Chain — BusRight procurement / fulfillment since 2024, framed as warehouse operations
// (NOT customer revenue; the bill-to is BusRight). The contract exposes order/return KPIs, an
// orders-by-type rollup, and an inventory snapshot. We surface a fulfillment funnel, an
// outbound-vs-returns split, and an inventory / reorder table (SIMs included). Where the contract
// has no per-day series or per-recipient rows yet, we say so rather than fabricate one.

// R1/R2 field-inventory + active-context bundle: three independent queries (component-flow,
// overview, telemetry) collapsed into one shape so the page body reads/branches on it once instead
// of threading four separate query results through its JSX.
interface FieldContext {
  readonly isLoading: boolean;
  readonly componentFlow: ComponentFlowPayload | null;
  readonly tabletsInField: number | null;
  readonly activeDevices: number | null;
  readonly totalDevices: number | null;
}

const useFieldContext = (): FieldContext => {
  const componentFlowQuery = useComponentFlow();
  const overviewQuery = useOverview();
  const telemetryQuery = useTelemetry();
  const stateMachine = telemetryQuery.data?.data.stateMachine ?? null;
  return {
    isLoading: componentFlowQuery.isLoading || overviewQuery.isLoading || telemetryQuery.isLoading,
    componentFlow: componentFlowQuery.data?.data ?? null,
    tabletsInField: overviewQuery.data?.data.kpis.fleetDistinctSerials.value ?? null,
    activeDevices: stateMachine?.fresh ?? null,
    totalDevices: stateMachine?.totalDevices ?? null,
  };
};

const ORDER_TYPE_LABEL: Record<OrderType, string> = {
  SO: "Sales / outbound (SO)",
  RM: "Return material (RM)",
  RR: "Return / restock (RR)",
  OTHER: "Other",
};

// Return order types per the contract (RM/RR are returns; SO is outbound).
const RETURN_TYPES: readonly OrderType[] = ["RM", "RR"];

// A SKU counts as a SIM line if its description reads like a cellular SIM — these are the reorder
// watch items the warehouse cares most about (a device without a SIM can't report).
const SIM_RE = /\bsim\b/i;
const isSim = (description: string): boolean => SIM_RE.test(description);

// Low-stock reorder threshold for the inventory watchlist callout.
const REORDER_THRESHOLD = 10;

// Corner radius for the funnel bars (rounded on the trailing/right edge only, matching the
// horizontal bar direction).
const FUNNEL_BAR_RADIUS: readonly [number, number, number, number] = [0, 4, 4, 0];

interface SupplyInsight {
  readonly headline: string;
  readonly stats: readonly InsightStat[];
}

// Compute the "so what" for supply: total returns (RM+RR shipments) and open RMAs, plus the
// highest-volume order type. Supply is volume, NOT health — severity stays NEUTRAL (never RAG).
const buildSupplyInsight = (
  kpis: SupplyKpis,
  ordersByType: readonly OrdersByTypeRow[],
): SupplyInsight => {
  const returns = kpis.returnShipments;
  const topType = ordersByType.reduce<OrdersByTypeRow | null>(
    (top, r) => (top === null || r.orderCount > top.orderCount ? r : top),
    null,
  );
  const topClause = topType
    ? ` Highest-volume order type: ${ORDER_TYPE_LABEL[topType.orderType]} (${formatInt(topType.orderCount)} orders).`
    : "";
  const headline = `${formatInt(returns)} return shipment${returns === 1 ? "" : "s"} and ${formatInt(kpis.openRmas)} open RMA${kpis.openRmas === 1 ? "" : "s"} against ${formatInt(kpis.outboundShipments)} outbound.${topClause}`;
  const stats: InsightStat[] = [
    { label: "outbound shipments", value: formatInt(kpis.outboundShipments) },
    { label: "returns + open RMAs", value: formatInt(returns + kpis.openRmas) },
    { label: "distinct recipients", value: formatInt(kpis.distinctRecipients) },
  ];
  return { headline, stats };
};

export const SupplyChainPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useSupply();
  const { scope } = useScope();
  const navigate = useNavigate();
  const meta = data?.meta;
  const payload = data?.data;
  const kpis = payload?.kpis;
  const insight = kpis && payload ? buildSupplyInsight(kpis, payload.ordersByType) : null;

  // R1/R2/R3/R4 — component-flow (tablet/cradle/harness/sim by month × purpose) + field-inventory /
  // active-device context. Independent of /api/supply so this bundle degrades on its own if any one
  // of its three endpoints is slow/down.
  const field = useFieldContext();

  // Outbound vs returns split from the KPI counts (always present), independent of whether the
  // orders-by-type rollup happens to carry return rows in this snapshot.
  const funnelOption = useMemo<EChartOption>(() => {
    const stages = kpis
      ? [
          { label: "Outbound shipments", value: kpis.outboundShipments },
          { label: "Return shipments", value: kpis.returnShipments },
          { label: "Open RMAs", value: kpis.openRmas },
        ]
      : [];
    const colors = sequentialColors(Math.max(1, stages.length));
    return {
      grid: { left: 130, right: 24, top: 8, bottom: 20 },
      tooltip: {
        trigger: "axis",
        backgroundColor: COLORS.surface2,
        borderColor: COLORS.border,
        textStyle: { color: COLORS.textPrimary },
      },
      xAxis: {
        type: "value",
        splitLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 10 },
      },
      yAxis: {
        type: "category",
        data: stages.map((s) => s.label),
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textSecondary, fontSize: 11 },
      },
      series: [
        {
          type: "bar",
          data: stages.map((s, i) => ({
            value: s.value,
            itemStyle: { color: colors[i], borderRadius: FUNNEL_BAR_RADIUS },
          })),
          barWidth: "52%",
        },
      ],
    };
  }, [kpis]);

  // Quantity by order type (outbound vs returns) as an illustrative distribution. The snapshot
  // commonly carries only SO; return types render as empty bins so the absence is visible.
  const qtyBins: DistributionBin[] = useMemo(() => {
    const rows = payload?.ordersByType ?? [];
    const byType = new Map(rows.map((r) => [r.orderType, r.totalQty]));
    const order: OrderType[] = ["SO", "RM", "RR", "OTHER"];
    return order.map((t) => ({
      label: t,
      count: byType.get(t) ?? 0,
      atRisk: RETURN_TYPES.includes(t),
    }));
  }, [payload]);

  // The snapshot can carry the same SKU description across multiple lot rows; aggregate by
  // description+warehouse so on-hand quantity is summed and each table row has a unique key.
  const inventory: readonly InventoryRow[] = useMemo(() => {
    const byKey = new Map<string, InventoryRow>();
    for (const r of payload?.inventory ?? []) {
      const key = `${r.description} ${r.warehouse}`;
      const prev = byKey.get(key);
      byKey.set(key, prev ? { ...prev, qtyAvailable: prev.qtyAvailable + r.qtyAvailable } : r);
    }
    return [...byKey.values()];
  }, [payload]);
  const invKey = (r: InventoryRow): string => `${r.description} ${r.warehouse}`;
  const inStock = inventory.filter((r) => r.qtyAvailable > 0);
  const sims = inStock.filter((r) => isSim(r.description));
  const lowStock = inStock.filter((r) => r.qtyAvailable <= REORDER_THRESHOLD);
  const topInventory = inStock.slice().sort((a, b) => b.qtyAvailable - a.qtyAvailable);

  if (isError) {
    return (
      <>
        <PageHeader title="Supply Chain" meta={meta} />
        <ChartCard title="Supply">
          <EmptyState
            title="Couldn't load supply chain"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Supply Chain"
        description="BusRight procurement & fulfillment since 2024 — warehouse cost, not customer revenue. Outbound vs returns, RMAs, and inventory / reorder."
        meta={meta}
      />

      {insight ? (
        <InsightCallout
          headline={insight.headline}
          stats={insight.stats}
          severity="neutral"
          cta={{ label: "Open Data Sources", to: "/data-sources" }}
        />
      ) : null}

      <ChartCard
        title="Field inventory & active context"
        subtitle="Current-state, not a month-end trend — tablets are the real Esper roster; cradle/harness/SIM are order-flow estimates"
        source="overview.kpis.fleetDistinctSerials + telemetry.stateMachine + supply.componentFlow"
        loading={field.isLoading}
      >
        {field.componentFlow &&
        field.tabletsInField !== null &&
        field.activeDevices !== null &&
        field.totalDevices !== null ? (
          <Drillable
            metricKey="fleet.deviceCount"
            scope={scope}
            label="Drill into field-inventory devices"
          >
            <ScFieldInventory
              componentFlow={field.componentFlow}
              tabletsInField={field.tabletsInField}
              activeDevices={field.activeDevices}
              totalDevices={field.totalDevices}
            />
          </Drillable>
        ) : (
          <EmptyState title="No field-inventory data" />
        )}
      </ChartCard>

      <div className="grid kpi-band" style={{ marginTop: 16 }}>
        <KpiCard
          label="Orders (lifetime)"
          icon={Boxes}
          loading={isLoading}
          value={kpis ? formatInt(kpis.totalOrders) : "—"}
          source="stg_e2_orders"
        />
        <KpiCard
          label="Outbound shipments"
          icon={Truck}
          loading={isLoading}
          value={kpis ? formatInt(kpis.outboundShipments) : "—"}
          source="stg_e2_shipments"
        />
        <KpiCard
          label="Return shipments"
          icon={Undo2}
          loading={isLoading}
          value={kpis ? formatInt(kpis.returnShipments) : "—"}
          source="stg_e2_shipments.order_type"
        />
        <KpiCard
          label="Open RMAs"
          icon={RotateCcw}
          loading={isLoading}
          value={kpis ? formatInt(kpis.openRmas) : "—"}
          source="stg_e2_rmas"
        />
      </div>

      <div className="grid cols-3">
        <ChartCard
          title="Fulfillment funnel"
          subtitle="Outbound → returns → open RMAs (shipment counts)"
          source="stg_e2_shipments"
          loading={isLoading}
          className="span-2"
        >
          {kpis ? <EChart option={funnelOption} height={180} /> : <EmptyState title="No funnel" />}
        </ChartCard>

        <ClickableCard to="/districts" label="Open Districts — recipient ↔ district match coverage">
          <ChartCard
            title="Ship-to / Recipient (district)"
            subtitle="Distinct ship-to locations served — open the recipient ↔ district view"
            source="stg_e2_orders.location_name"
            loading={isLoading}
          >
            {kpis ? (
              <div className="metric-strip">
                <div className="metric-chip">
                  <span className="mc-label">
                    <Users size={13} aria-hidden /> Distinct recipients
                  </span>
                  <span className="mc-value br-tabular-nums">
                    {formatInt(kpis.distinctRecipients)}
                  </span>
                </div>
                <div className="metric-chip">
                  <span className="mc-label">
                    <PackageCheck size={13} aria-hidden /> Orders / recipient (avg)
                  </span>
                  <span className="mc-value br-tabular-nums">
                    {(kpis.totalOrders / Math.max(1, kpis.distinctRecipients)).toFixed(1)}
                  </span>
                </div>
              </div>
            ) : (
              <EmptyState title="No recipient data" />
            )}
          </ChartCard>
        </ClickableCard>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Order quantity by type"
          subtitle="Outbound (SO) vs returns (RM/RR) — return bins highlighted"
          source="stg_e2_orders.order_type"
          loading={isLoading}
        >
          {payload && payload.ordersByType.length > 0 ? (
            <Distribution bins={qtyBins} riskColor={COLORS.accent} height={150} />
          ) : (
            <EmptyState title="No order-type rollup" />
          )}
        </ChartCard>

        <ChartCard
          title="Orders by type"
          subtitle="Order count and total quantity"
          source="stg_e2_orders.order_type"
          loading={isLoading}
          className="span-2"
        >
          <DataTable
            rows={payload?.ordersByType ?? []}
            rowKey={(r) => r.orderType}
            emptyTitle="No order types"
            onRowClick={() => void navigate("/data-sources")}
            columns={[
              {
                key: "type",
                header: "Type",
                render: (r) => <span>{ORDER_TYPE_LABEL[r.orderType]}</span>,
              },
              {
                key: "orders",
                header: "Orders",
                align: "right",
                render: (r) => <span className="mono">{formatInt(r.orderCount)}</span>,
              },
              {
                key: "qty",
                header: "Total qty",
                align: "right",
                render: (r) => <span className="mono">{formatInt(r.totalQty)}</span>,
              },
            ]}
          />
        </ChartCard>
      </div>

      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <ChartCard
          title="Purchases by month × component"
          subtitle="Tablet counts are real order flow; cradle / harness / SIM are description-parsed estimates"
          source="supply.componentFlow (rpt_sc_component_flow_monthly)"
          loading={field.isLoading}
        >
          {field.componentFlow && field.componentFlow.rows.length > 0 ? (
            <Drillable
              metricKey="supply.orderQty"
              scope={scope}
              label="Drill into monthly component purchases"
            >
              <ScComponentPurchases rows={field.componentFlow.rows} />
            </Drillable>
          ) : (
            <EmptyState title="No component-flow data" />
          )}
        </ChartCard>

        <ChartCard
          title="Replacements by month × component"
          subtitle="Replacement + Pick-and-Pack order lines, by component"
          source="supply.componentFlow · isReplacementClass"
          loading={field.isLoading}
        >
          <MetricLabel
            metricKey="device.replacementRatePct"
            label="Replacement + Pick-and-Pack (R4)"
          />
          {field.componentFlow?.rows.some((r) => r.isReplacementClass) ? (
            <Drillable
              metricKey="device.replacementRatePct"
              scope={scope}
              label="Drill into monthly replacement flow"
            >
              <ScReplacementFlow rows={field.componentFlow.rows} />
            </Drillable>
          ) : (
            <EmptyState title="No replacement-class order lines" />
          )}
        </ChartCard>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Inventory & reorder"
          subtitle="SIM stock and low-stock watch items"
          source="stg_e2_inventory"
          loading={isLoading}
        >
          {inStock.length > 0 ? (
            <div className="metric-strip">
              <div className="metric-chip">
                <span className="mc-label">SKUs in stock</span>
                <span className="mc-value br-tabular-nums">{formatInt(inStock.length)}</span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">SIMs available (total)</span>
                <span className="mc-value br-tabular-nums">
                  {formatInt(sims.reduce((sum, r) => sum + r.qtyAvailable, 0))}
                </span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Low-stock SKUs (≤{REORDER_THRESHOLD})</span>
                <span className="mc-value warn br-tabular-nums">{formatInt(lowStock.length)}</span>
              </div>
            </div>
          ) : (
            <EmptyState title="No inventory in stock" />
          )}
        </ChartCard>

        <ChartCard
          title="Inventory on hand"
          subtitle="Available quantity by SKU (in-stock SKUs, highest first)"
          source="stg_e2_inventory.qty_available"
          loading={isLoading}
          className="span-2"
        >
          <DataTable
            rows={topInventory}
            rowKey={invKey}
            emptyTitle="No inventory"
            columns={[
              {
                key: "desc",
                header: "SKU",
                render: (r) => <span>{r.description}</span>,
              },
              {
                key: "wh",
                header: "Warehouse",
                render: (r) => <span className="mono">{r.warehouse}</span>,
              },
              {
                key: "qty",
                header: "Available",
                align: "right",
                render: (r) => (
                  <span
                    className="mono"
                    style={
                      r.qtyAvailable <= REORDER_THRESHOLD
                        ? { color: COLORS.textSecondary }
                        : undefined
                    }
                  >
                    {formatInt(r.qtyAvailable)}
                  </span>
                ),
              },
            ]}
          />
        </ChartCard>
      </div>

      {meta ? (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Data notes" loading={isLoading}>
            <NotesList notes={meta.notes} />
          </ChartCard>
        </div>
      ) : null}
    </>
  );
};
